import { IAvailableBookingPeriod } from '@shared/integrations/taf-api/v2/models';
import AccommodationBookingSummary from './AccommodationBookingSummary';
import {
    usePostApiV2ObjectBookingObjectsObjectIdCreateBooking,
    usePostApiV2ObjectBookingObjectsObjectIdValidateBookingPeriod,
} from '@shared/integrations/taf-api/v2/features/object-booking/object-booking';
import Loader from '@shared/components/Loader';
import AlertBox from '@shared/components/AlertBox.tsx';
import AccommodationBookingError from '@my-pages/features/object-booking/components/AccommodationBookingError.tsx';
import { useEffect, useRef, useState } from 'react';
import { useGetUmbracoTafDeliveryApiV1ContentItemPathSuspense } from '@shared/integrations/umbraco-taf-delivery-api/features/content/content.ts';
import { AccommodationBookingGroupPageContentResponseModel } from '@shared/integrations/umbraco-taf-delivery-api/models';
import Markup from '@shared/components/Markup.tsx';
import AccommodationToEarlyBookingError from '@my-pages/features/object-booking/components/AccommodationToEarlyBookingError.tsx';
import { Link, useRouter } from '@tanstack/react-router';

interface IAccommodationConfirmBookingDialog {
    period?: IAvailableBookingPeriod;
    handleGetAvailablePeriods: (date: Date) => void;
    bookingGroupUrl: string;
}

const isThirtyDaysAhead = (dateToCheck?: Date) => {
    if (!dateToCheck) return true;

    const today = new Date();
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + 30);

    // Normalize both dates to the same time (e.g., midnight) if comparing just the date part
    const normalize = (date: Date) => {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    return normalize(dateToCheck).getTime() > normalize(targetDate).getTime();
};

const AccommodationConfirmBookingDialog = ({ handleGetAvailablePeriods, period, bookingGroupUrl }: IAccommodationConfirmBookingDialog) => {
    const router = useRouter();
    const { data: cabinGroup } = useGetUmbracoTafDeliveryApiV1ContentItemPathSuspense<AccommodationBookingGroupPageContentResponseModel>(bookingGroupUrl);
    const [approvedTerms, setApprovedTerms] = useState<boolean>(false);
    const [toEarlyBookingError, setToEarlyBookingError] = useState<boolean>(false);

    const {
        mutate: validatePeriod,
        isPending: isValidating,
        error: validationError,
        isError: hasValidationErrors,
        reset: resetValidateBooking,
    } = usePostApiV2ObjectBookingObjectsObjectIdValidateBookingPeriod();

    const { mutate: createBooking, isPending: isCreating, error: creationError, isSuccess: creationSuccess, reset: resetCreateBooking } = usePostApiV2ObjectBookingObjectsObjectIdCreateBooking();

    const handleCreateBooking = () => {
        if (!period || !period.objectId) return;
        createBooking(
            { objectId: period.objectId, data: period },
            {
                onSuccess: () => {
                    if (period.startDate) handleGetAvailablePeriods(new Date(period.startDate));
                },
            },
        );
    };
    const handleValidateBooking = () => {
        setToEarlyBookingError(false);
        if (!period || !period.objectId) return;
        const isAHead = period.startDate ? isThirtyDaysAhead(new Date(period.startDate)) : true;

        if (isAHead) validatePeriod({ objectId: period.objectId, data: period });
        else setToEarlyBookingError(true);
    };

    const handleOnClose = () => {
        resetCreateBooking();
        resetValidateBooking();
        setApprovedTerms(false);
    };

    const ref = useRef<HTMLDivElement>(null);
    const closeModalRef = useRef<HTMLButtonElement>(null);

    const closeModal = (e: any) => {
        e.preventDefault(); // Prevent the default link action
        closeModalRef.current?.click();
        setTimeout(() => {
            window.location.href = e.target.href; // Navigate after action
        }, 600);
    };

    useEffect(() => {
        if (ref.current) {
            $(ref.current).on('show.bs.modal', handleValidateBooking);
            $(ref.current).on('hidden.bs.modal', handleOnClose);
        }

        return () => {
            if (ref.current) {
                $(ref.current).off('show.bs.modal', handleValidateBooking);
                $(ref.current).off('hidden.bs.modal', handleOnClose);
            }
        };
    }, [period]);

    return (
        <div
            ref={ref}
            className='modal fade'
            id='accommondationConfirmBooking'
            tabIndex={-1}
            aria-labelledby='accommondationConfirmBookingLabel'
            aria-hidden='true'
        >
            <div className='modal-dialog modal-lg'>
                <div className='modal-content'>
                    <div className='modal-header'>
                        <h5
                            className='modal-title'
                            id='accommondationConfirmBookingLabel'
                        >
                            {creationSuccess ? cabinGroup.properties?.bookingCompletedHeading : cabinGroup.properties?.bookingConfirmationHeading}
                        </h5>
                        <button
                            type='button'
                            className='btn btn-light'
                            data-dismiss='modal'
                            aria-label='Close'
                        >
                            <span aria-hidden='true'>&times;</span>
                        </button>
                    </div>
                    <div className='modal-body'>
                        {isValidating || isCreating ? (
                            <Loader />
                        ) : (
                            <>
                                {creationSuccess ? (
                                    <AlertBox
                                        className='mb-4'
                                        theme='success'
                                    >
                                        <Markup content={cabinGroup.properties?.bookingCompletedText?.markup} />
                                    </AlertBox>
                                ) : (
                                    <>
                                        {creationError || validationError || toEarlyBookingError ? (
                                            <>
                                                {creationError ? <AccommodationBookingError errors={creationError} /> : null}
                                                {validationError ? <AccommodationBookingError errors={validationError} /> : null}
                                                {toEarlyBookingError ? <AccommodationToEarlyBookingError /> : null}
                                            </>
                                        ) : (
                                            <>
                                                {cabinGroup.properties?.bookingConfirmationInfo ? (
                                                    <AlertBox
                                                        className='mb-4'
                                                        theme='info'
                                                    >
                                                        <Markup content={cabinGroup.properties?.bookingConfirmationInfo.markup} />
                                                    </AlertBox>
                                                ) : null}
                                                {period ? (
                                                    <>
                                                        <AccommodationBookingSummary period={period} />
                                                    </>
                                                ) : (
                                                    <AlertBox
                                                        className='mb-4'
                                                        heading='Fel vid bokning'
                                                        theme='danger'
                                                    >
                                                        <p>Kunde inte verifiera vald period. Stäng fönstret och prova igen.</p>
                                                    </AlertBox>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                    <div className='modal-footer flex-column flex-md-row'>
                        {cabinGroup.properties?.acceptBookingPolicyText && !creationSuccess && !validationError && !isValidating && !toEarlyBookingError ? (
                            <div className='flex-1'>
                                <label className='custom-input mt-3'>
                                    <input
                                        type='checkbox'
                                        id='approveTerms'
                                        aria-checked='false'
                                        aria-required='true'
                                        checked={approvedTerms}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setApprovedTerms(e.target.checked)}
                                    />
                                    <span
                                        className='custom-input-label'
                                        id='termsSummary'
                                    >
                                        <Markup content={cabinGroup.properties?.acceptBookingPolicyText.markup} />
                                        <Link
                                            to={router.state.location.pathname}
                                            onClick={closeModal}
                                            hash='cancellationPolicy'
                                        >
                                            {cabinGroup.properties?.bookingPolicyReadMoreLinkText}
                                        </Link>
                                    </span>
                                </label>
                            </div>
                        ) : null}
                        <div className='d-flex flex-wrap gap-2'>
                            {isCreating || isValidating || creationError || hasValidationErrors || creationSuccess || toEarlyBookingError ? null : (
                                <button
                                    disabled={!approvedTerms}
                                    type='button'
                                    className='btn btn-primary'
                                    onClick={() => {
                                        handleCreateBooking();
                                    }}
                                >
                                    Boka
                                </button>
                            )}

                            <button
                                ref={closeModalRef}
                                disabled={isCreating || isValidating}
                                type='button'
                                className='btn btn-light'
                                data-dismiss='modal'
                            >
                                {creationSuccess ? 'Stäng' : 'Avbryt'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default AccommodationConfirmBookingDialog;
